import React, { useEffect, useRef, useState } from "react";
import PixelGlyph from "../components/PixelGlyph";
import { beginDrag, clamp, useIsDesktop } from "../utils/desktop";
import { useVolume } from "../components/VolumeContext";

const TRACK = { src: "/las-palomitas.mp3", name: "las palomitas.mp3" };

const GLYPHS = {
  play: ["#......", "###....", "#####..", "#######", "#####..", "###....", "#......"],
  pause: ["##.##", "##.##", "##.##", "##.##", "##.##", "##.##", "##.##"],
  stop: ["#######", "#######", "#######", "#######", "#######", "#######", "#######"],
};

const formatTime = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const whole = Math.floor(s);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const PlayerButton = ({ label, glyph, onClick }) => (
  <button
    className="w-9 h-7 flex items-center justify-center bg-[#c0c0c0] border-[0.13rem] border-t-white border-l-white border-b-black border-r-black active:border-t-black active:border-l-black active:border-b-white active:border-r-white"
    aria-label={label}
    title={label}
    onClick={onClick}
  >
    <PixelGlyph rows={glyph} />
  </button>
);

const MediaPlayer = () => {
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(NaN);
  const isDesktop = useIsDesktop();
  const { level, muted } = useVolume();

  // Closing the window unmounts the player; make sure the music stops with it.
  useEffect(() => {
    const audio = audioRef.current;
    return () => audio.pause();
  }, []);

  // The window is hidden without a desktop, so pause rather than play from nowhere; the position is kept.
  useEffect(() => {
    if (!isDesktop) audioRef.current.pause();
  }, [isDesktop]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.volume = level;
    audio.muted = muted;
  }, [level, muted]);

  // Opening the file starts playback, like double-clicking an mp3. The double-click that opened the
  // window counts as the user gesture browsers require for audio. Declared after the volume effect so
  // the first note already plays at the taskbar volume.
  useEffect(() => {
    audioRef.current.play().catch((err) => console.error("Failed to autoplay audio:", err));
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play().catch((err) => console.error("Failed to play audio:", err));
    } else {
      audio.pause();
    }
  };

  const stop = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
  };

  const handleSeekPointerDown = (e) => {
    if (!Number.isFinite(duration)) return;
    const audio = audioRef.current;
    const b = trackRef.current.getBoundingClientRect();
    const x0 = e.clientX - b.left;
    const seek = (x) => {
      audio.currentTime = (clamp(x, 0, b.width) / b.width) * duration;
    };

    seek(x0);
    beginDrag(e, (dx) => seek(x0 + dx), () => {});
  };

  const progress = Number.isFinite(duration) && duration > 0 ? (time / duration) * 100 : 0;

  return (
    <div className="flex flex-col h-full desktop-font text-xl select-none">
      <audio
        ref={audioRef}
        src={TRACK.src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      <div className="flex flex-col flex-1 justify-center gap-3 px-3 pb-2">
        {/* Seek Bar */}
        <div
          ref={trackRef}
          className="relative h-6 cursor-pointer touch-none"
          onPointerDown={handleSeekPointerDown}
        >
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[0.3rem] bg-white border-[0.1rem] border-t-[#828282] border-l-[#828282] border-b-white border-r-white" />
          <div
            className="absolute top-0 h-full w-[0.7rem] -translate-x-1/2 bg-[#c0c0c0] border-[0.13rem] border-t-white border-l-white border-b-black border-r-black"
            style={{ left: `${progress}%` }}
          />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <PlayerButton
            label={playing ? "Pause" : "Play"}
            glyph={playing ? GLYPHS.pause : GLYPHS.play}
            onClick={togglePlay}
          />
          <PlayerButton label="Stop" glyph={GLYPHS.stop} onClick={stop} />
          <div className="flex flex-1 min-w-0 items-center justify-between h-7 ml-2 px-2 bg-white border-[0.1rem] border-t-[#828282] border-l-[#828282] border-b-white border-r-white">
            <span className="truncate mt-[0.2rem]">{TRACK.name}</span>
            <span className="shrink-0 ml-2 mt-[0.2rem]">
              {formatTime(time)} / {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPlayer;
